import FingerprintJS from '@fingerprintjs/fingerprintjs'
import getTimezones from 'api/getTimezones'
import registerByFacebook from 'api/registerByFacebook'
import registerByGoogle from 'api/registerByGoogle'
import { PlausibleEvent, RegistrationTypes } from 'enums/plausible'
import ErrorCode from 'forms/ErrorCodes'
import GoogleForm from 'forms/GoogleForm'
import handleResponseError from 'forms/handleResponseError'
import validateMessage from 'forms/validateMessage'
import Cookies from 'js-cookie'
import { useCallback, useEffect, useState } from 'react'
import getReferralCookies from 'utils/getReferralCookies'
import useLanguageForSave from 'utils/hooks/useLanguageForSave'
import usePlausible from 'utils/hooks/usePlausible'
import { useInfoProviderContext } from 'utils/providers/InfoProvider/InfoProviderContext'
import setTokensAndRedirect from 'utils/setTokensAndRedirect'

import { useTranslation } from 'next-i18next'
import { serverSideTranslations } from 'next-i18next/serverSideTranslations'
import dynamic from 'next/dynamic'
import Head from 'next/head'
import { useRouter } from 'next/router'

import AuthLayout from 'layouts/AuthLayout'

import { FacebookIcon, GoogleIcon } from 'components/Images/Icons'
import Section from 'components/Section/Section'

import styles from './Social.module.scss'

const DynamicRestrictedModal = dynamic(() => import('components/RestrictedModal'))

const RegisterBySocialPage = ({ referrer }) => {
  const router = useRouter()
  const { providerUserToken, social } = router.query

  const [timezone, setTimezone] = useState()

  const setEvent = usePlausible()

  const [t, i18n] = useTranslation(['main'])
  const savedLanguage = useLanguageForSave(i18n)

  useEffect(
    () =>
      setEvent(PlausibleEvent.OpenedCurrencySelecting, {
        step: 3,
        referrer,
        source: router.query?.from as string,
        social: social as string,
      }),
    [],
  )

  useEffect(() => {
    const loadTimezones = async () => {
      const res = await getTimezones()
      const timezones = res.data.payload.items
      const currentTimezoneName = Intl.DateTimeFormat().resolvedOptions().timeZone
      const found = timezones.find((tz) => {
        return tz.name === currentTimezoneName
      })
      setTimezone(found.id)
    }

    loadTimezones()
  }, [setTimezone])

  const [error, setError] = useState('')
  const onSubmit = useCallback(
    async (values) => {
      setEvent(PlausibleEvent.ClickedSignUp, {
        step: 2,
        referrer,
        source: router.query?.from as string,
        currency: values.currency.value,
        email: values.email,
      })
      try {
        const token = Array.isArray(providerUserToken)
          ? providerUserToken.join(',')
          : providerUserToken

        const fp = await FingerprintJS.load()
        const fpResponse = await fp.get()

        const registerBySocialRequest = {
          google: registerByGoogle,
          facebook: registerByFacebook,
        }

        const res = await registerBySocialRequest[social as string]({
          timezone,
          visitorId: fpResponse.visitorId,
          providerUserToken: token,
          preferredCurrency: values.currency.value,
          language: savedLanguage,
          ...getReferralCookies(),
        })

        if (res.data.status !== 'success') {
          const { code, payload } = res.data
          switch (code) {
            case ErrorCode.VALIDATION_ERRORS:
              if (payload?.errors) {
                const wrappedErrors = handleResponseError(res)
                setError(wrappedErrors)
              }
              break
            case ErrorCode.EMAIL_ALREADY_REGISTER:
              setError(validateMessage.emailIsAlreadyRegistered)
              break
            case ErrorCode.DUPLICATE_CLICK_ID:
            case ErrorCode.DUPLICATE_GA_CLICK_ID:
              setError(validateMessage.youAlreadyHaveRegisteredAccount)
              break
            case ErrorCode.CURRENCY_NOT_FOUND:
              setError(validateMessage.currencyNotFound)
              break
            case ErrorCode.GEO_RESTRICTED:
              setError(validateMessage.restricted)
              break
            case ErrorCode.SOCIAL_DUPLICATE_USER_EMAIL:
              setError(validateMessage.duplicateUserEmail)
              break
            default:
              setError(validateMessage.serverError)
          }

          setEvent(PlausibleEvent.NotRegistered, {
            type: 'system',
            step: 3,
            referrer,
            source: router.query?.from as string,
            error,
            userCurrency: values.currency.value,
          })
        } else {
          setError('')

          if (Cookies.get('registrationSourceId')) {
            Cookies.set('justRegisteredOnPredictrion', 'true')
            localStorage.setItem('justRegisteredOnPredictrion', 'true')
            Cookies.remove('registrationSourceId')
          } else {
            Cookies.set('justRegistered', 'true')
            localStorage.setItem('justRegistered', 'true')
          }

          setEvent(PlausibleEvent.Registered, {
            step: 3,
            type: 'system',
            referrer,
            source: router.query?.from as string,
            userCurrency: values.currency.value,
          })
          setEvent(PlausibleEvent.UserRegistered, {
            userId: res.data.payload?.id,
            referrer,
            source: window.location.host,
            type: RegistrationTypes.Google,
          })

          setTokensAndRedirect(res.data.payload.accessToken, res.data.payload.refreshToken)
        }
      } catch (e) {
        setError(validateMessage.serverError)
      }
    },
    [setError, providerUserToken, timezone, savedLanguage, social],
  )

  const info = useInfoProviderContext()

  if (info?.restricted === true) {
    return <DynamicRestrictedModal />
  }

  return (
    <>
      <Head>
        <title>Sign In | Binany</title>
        <meta name="description" content="Sign in for trading on Binany" />

        <meta property="og:title" content="Sign In | Binany" />
        <meta property="og:description" content="Sign in for trading on Binany" />
        <meta property="og:image" content="https://binany.com/images/logoWhite.svg" />
        <meta property="og:site_name" content="Binany - Online Platform for Trading" />
        <meta property="og:type" content="article" />
        <meta property="og:url" content="https://binany.com/auth/signup" />
      </Head>
      <AuthLayout>
        <Section>
          <div className={styles.grid}>
            <div className={styles.header}>{t('main:googleSignUp')}</div>
            <div className={styles.tip}>{t('main:googleSocialNetwork')}</div>
            <div className={styles.recovery_icon}>
              {social === 'google' ? <GoogleIcon /> : <FacebookIcon />}
            </div>

            <div className={styles.form}>
              <GoogleForm error={error} onSubmit={onSubmit} />
            </div>
          </div>
        </Section>
      </AuthLayout>
    </>
  )
}

export default RegisterBySocialPage

export async function getServerSideProps({ locale }) {
  return {
    props: {
      ...(await serverSideTranslations(locale, ['main', 'contacts', 'shared', 'error'])),
    },
  }
}
